import { bytesToBase64 } from './bytes-to-base64.js';

/**
 * Rückgabe von `generatePatientKeypair`: Public-Key als SPKI/Base64 (geht in
 * den Payload) und nicht-extrahierbarer Private-Key (geht in IndexedDB).
 */
interface PatientKeypair {
  readonly publicKeyBase64: string;
  readonly privateKey: CryptoKey;
}

const KEY_ALGORITHM: RsaHashedKeyGenParams = {
  name: 'RSA-OAEP',
  modulusLength: 3072,
  publicExponent: new Uint8Array([0x01, 0x00, 0x01]),
  hash: 'SHA-256',
};

const IMPORT_ALGORITHM: RsaHashedImportParams = {
  name: 'RSA-OAEP',
  hash: 'SHA-256',
};

const PRIVATE_KEY_EXTRACTABLE = false;
// `unwrapKey` ist Pflicht, weil `decryptResponse` den AES-Session-Key mit
// `crypto.subtle.unwrapKey` aus dem Tenant-Response-Payload herauszieht.
// Ohne diese Usage wirft WebCrypto `InvalidAccessError`, was sich im UI als
// `decrypt_failed` zeigt (Tech-Error trotz `status: success` vom Mock).
const PRIVATE_KEY_USAGES: readonly KeyUsage[] = ['unwrapKey'];

/**
 * Erzeugt ein RSA-OAEP-3072-Keypair für den Patienten.
 *
 * Strategie (Audit-Empfehlung F5/F8): Der Private-Key MUSS am Ende
 * `extractable: false` haben, damit XSS ihn nicht exfiltrieren kann.
 * WebCrypto.generateKey setzt jedoch einen gemeinsamen `extractable`-Flag
 * für beide Hälften — wir bräuchten den Public-Key extractable, um ihn als
 * SPKI zu serialisieren.
 *
 * Lösung: Wir generieren mit `extractable: true`, exportieren sofort den
 * Public-Key als SPKI/Base64, exportieren den Private-Key als JWK und
 * re-importieren ihn mit `extractable: false`. Die kurzlebige
 * extractable-Variante wird nicht zurückgegeben und vom GC eingesammelt.
 *
 * Audit-Notiz (DEV-013 LOW-2): Der JWK-Blob aus `exportKey('jwk', ...)`
 * enthält das RSA-Private-Material (`d`, `p`, `q`, `dp`, `dq`, `qi`) als
 * Strings im V8-Heap. JavaScript bietet keinen deterministischen Memory-
 * Wipe, daher bleibt das Restrisiko, dass ein XSS unmittelbar nach dem
 * Generieren das JWK aus dem Heap exfiltriert. Wir akzeptieren dieses
 * Zeitfenster bewusst, weil es ohne native Wipe-Primitive nicht
 * geschlossen werden kann.
 */
export async function generatePatientKeypair(): Promise<PatientKeypair> {
  const ephemeralPair = await crypto.subtle.generateKey(KEY_ALGORITHM, true, ['unwrapKey']);
  const publicKeyBase64 = await exportSpkiBase64(ephemeralPair.publicKey);
  const privateKey = await reimportNonExtractable(ephemeralPair.privateKey);
  return { publicKeyBase64, privateKey };
}

async function exportSpkiBase64(publicKey: CryptoKey): Promise<string> {
  const spkiBuffer = await crypto.subtle.exportKey('spki', publicKey);
  return bytesToBase64(new Uint8Array(spkiBuffer));
}

async function reimportNonExtractable(ephemeralPrivateKey: CryptoKey): Promise<CryptoKey> {
  const jwk = await crypto.subtle.exportKey('jwk', ephemeralPrivateKey);
  return crypto.subtle.importKey('jwk', jwk, IMPORT_ALGORITHM, PRIVATE_KEY_EXTRACTABLE, [
    ...PRIVATE_KEY_USAGES,
  ]);
}
